/**
 * Owner: Masa Bayu
 * File: batch01-evidence.ts
 * Date:Time: 2026-09-19 19:59
 * Description: Batch 1 evidence packs — arabic · hebrew · READY · not Supported.
 */
import { QX_BATCH_01_EVIDENCE_AREAS, QX_BATCH_01_MODEL_VERSION, QX_BATCH_01_OWNER_ACCEPTED_AT, QX_GL_CERT_GLOBAL_BATCH_01_ID, } from './batch01-codes';
import { fixturesForBatch01Family, listBatch01FixtureIds, QX_BATCH_01_ARABIC_SAMPLE_LANGUAGES, QX_BATCH_01_HEBREW_SAMPLE_LANGUAGES, QX_BATCH_01_TEXT_SAMPLES, } from './batch01-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxCertificationRecord, } from './certification-record';
import { assertMayClaimSupported, createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { createQxMultilingualContentModel, applyOverrideWithoutChangingContentId, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
const FAMILY_CFG = Object.freeze({
    arabic: Object.freeze({
        scriptId: 'Arab',
        primary: 'ar',
        samples: QX_BATCH_01_ARABIC_SAMPLE_LANGUAGES,
        analyzer: 'arabic_script',
        sampleText: QX_BATCH_01_TEXT_SAMPLES.ar,
        secondary: 'fa',
        contentId: 'doc-arabic-batch01-01',
    }),
    hebrew: Object.freeze({
        scriptId: 'Hebr',
        primary: 'he',
        samples: QX_BATCH_01_HEBREW_SAMPLE_LANGUAGES,
        analyzer: 'hebrew_script',
        sampleText: QX_BATCH_01_TEXT_SAMPLES.he,
        secondary: 'yi',
        contentId: 'doc-hebrew-batch01-01',
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_01_EVIDENCE_AREAS) {
        if (!areas[key]) {
            return false;
        }
    }
    return true;
}
/**
 * Build one Batch 1 family evidence pack.
 * Does not stamp Owner Accept or allow Supported.
 */
export function buildBatch01FamilyEvidenceBundle(familyId) {
    const cfg = FAMILY_CFG[familyId];
    const areas = {};
    const fixtures = fixturesForBatch01Family(familyId);
    const primary = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    const secondary = createQxLanguageIdentity({
        languageId: cfg.secondary,
        plane: 'publication',
    });
    areas.identity =
        primary.languageId === cfg.primary &&
            secondary.languageId === cfg.secondary &&
            cfg.samples.includes(cfg.primary);
    const scriptRel = resolveLanguageScriptRelation(cfg.primary);
    areas.script = scriptRel.resolvedScriptId === cfg.scriptId;
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'rtl');
    areas.direction =
        dir.hintedDirection === 'rtl' && dirOverride.override === 'rtl';
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages: [...cfg.samples],
        overrides: [
            {
                scope: 'inline',
                spanId: `quote-${cfg.secondary}`,
                languageId: cfg.secondary,
            },
        ],
    });
    areas.content =
        content.primaryLanguageId === cfg.primary &&
            content.contentLanguages.includes(cfg.secondary);
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages: [...cfg.samples],
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.scriptId &&
            preserved.defaultDirection === 'rtl';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages: [cfg.primary, cfg.secondary],
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'rtl';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.secondary,
    });
    areas.reader =
        typo.scriptId === cfg.scriptId &&
            heading.scriptId === cfg.scriptId &&
            typo.shapingRequired === true &&
            typo.fallbackChain.length > 0;
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_01_TEXT_SAMPLES.composed, 'NFC');
    areas.search =
        profile.analyzerFamily === cfg.analyzer &&
            prep.segmentation.strategy !== undefined &&
            nfc.normalized === 'café' &&
            nfc.original === QX_BATCH_01_TEXT_SAMPLES.composed;
    const wm = resolveWritingModeHint(cfg.primary);
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' && heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: [
            {
                spanId: `quote-${cfg.secondary}`,
                languageId: cfg.secondary,
                boundary: 'inline',
            },
        ],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'rtl' &&
            a11y.spanSemantics[0]?.langTag === cfg.secondary &&
            a11y.readingOrderPreserved === true;
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'rtl' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    const next = applyOverrideWithoutChangingContentId(content, {
        scope: 'block',
        spanId: `note-${cfg.secondary}`,
        languageId: cfg.secondary,
    }, cfg.contentId);
    areas.regression =
        next.contentId === content.contentId && next.overrides.length === 2;
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_01_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_01_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [cfg.scriptId],
        sampleLanguageIds: [...cfg.samples],
        directions: ['rtl'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch01FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_01_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_01_ID,
        familyId,
        scriptId: cfg.scriptId,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch01FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 1 evidence READY — not Supported (awaiting Owner Accept)`
            : `${familyId} Batch 1 evidence incomplete`,
    });
}
export function buildBatch01EvidenceReport() {
    const arabic = buildBatch01FamilyEvidenceBundle('arabic');
    const hebrew = buildBatch01FamilyEvidenceBundle('hebrew');
    const ready = arabic.readyForOwnerAccept &&
        hebrew.readyForOwnerAccept &&
        arabic.automatedTestsPass &&
        hebrew.automatedTestsPass;
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_01_ID,
        modelVersion: QX_BATCH_01_MODEL_VERSION,
        arabic,
        hebrew,
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: Object.freeze(['latin-core']),
    });
}
export function assertBatch01FamilyNotSupported(bundle) {
    if (bundle.supportedClaimAllowed !== false || bundle.claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `FORBIDDEN: ${bundle.familyId} Supported claim before Owner Accept`);
    }
    if (bundle.row.level === 'certified' && bundle.row.ownerAcceptedAt === null) {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: certified without ownerAcceptedAt');
    }
}
export function assertBatch01NotAllSupported(report) {
    assertBatch01FamilyNotSupported(report.arabic);
    assertBatch01FamilyNotSupported(report.hebrew);
    if (report.batchOpenImpliesSupported) {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: Batch 1 OPEN must not imply Supported');
    }
    if (report.supportedFamilyIdsStill.length !== 1 ||
        report.supportedFamilyIdsStill[0] !== 'latin-core') {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: Batch 1 must not expand Supported beyond latin-core');
    }
}
/**
 * Owner Accept → Certified + Supported records for arabic · hebrew.
 */
export function createBatch01CertifiedRecords(ownerAcceptedAt = QX_BATCH_01_OWNER_ACCEPTED_AT) {
    const report = buildBatch01EvidenceReport();
    if (!report.readyForOwnerAccept) {
        throw new QxLanguageError('INVALID_INPUT', 'Batch 1 evidence packs not READY for Owner Accept');
    }
    const records = [];
    for (const familyId of ['arabic', 'hebrew']) {
        const bundle = familyId === 'arabic' ? report.arabic : report.hebrew;
        if (!bundle.a11ySpotCheckPass || !bundle.manualQaChecklistPackaged) {
            throw new QxLanguageError('INVALID_INPUT', `${familyId} Certified requires a11y + manual QA checklist`);
        }
        const samples = familyId === 'arabic'
            ? QX_BATCH_01_ARABIC_SAMPLE_LANGUAGES
            : QX_BATCH_01_HEBREW_SAMPLE_LANGUAGES;
        const record = createQxCertificationRecord({
            familyId,
            scriptIds: [bundle.scriptId],
            sampleLanguageIds: [...samples],
            directions: ['rtl'],
            fixtures: listBatch01FixtureIds(familyId),
            automatedTestsPass: true,
            manualQaChecklistPass: true,
            a11ySpotCheckPass: true,
            ownerAcceptedAt,
            evidenceRefs: listBatch01FixtureIds(familyId),
        });
        assertMayClaimSupported(record.row);
        if (supportClaimForRow(record.row) !== 'supported') {
            throw new QxLanguageError('INVALID_INPUT', `${familyId} Certified record must allow Supported claim`);
        }
        records.push(record);
    }
    return Object.freeze(records);
}
//# sourceMappingURL=batch01-evidence.js.map